/**
 * Correlation matrices: Pearson or Spearman, with the p-value and sample size of every
 * pair.
 *
 * Missing values are handled one of two ways, as in R's `cor(use = …)`:
 *
 * - **pairwise** — each pair uses every case that has both values, so the n differs from
 *   cell to cell (`use = "pairwise.complete.obs"`);
 * - **listwise** — only cases complete on every variable are used, so n is the same
 *   throughout (`use = "complete.obs"`).
 *
 * Spearman ranks (with average ranks for ties) are computed within each pair's cases.
 * Each p-value is the t test on r with n − 2 degrees of freedom — exact for Pearson under
 * normality, and the standard approximation for Spearman (the `exact = FALSE` p-value of
 * `cor.test`). With many variables the pairs are many tests, so Bonferroni and Holm
 * adjustments across the k(k − 1)/2 distinct pairs are offered (R's `p.adjust`).
 */

import { averageRanks, pearson } from "@/lib/stats/descriptive";
import { studentTTwoTailed } from "@/lib/stats/distributions";

export type CorrelationMethod = "pearson" | "spearman";
export type MissingMode = "pairwise" | "listwise";
export type Adjustment = "none" | "bonferroni" | "holm";

export interface MatrixReport {
  k: number;
  method: CorrelationMethod;
  missing: MissingMode;
  adjustment: Adjustment;
  /** r[i][j]; the diagonal is 1 and an undefined pair is NaN. */
  r: number[][];
  /** Raw two-sided p-values; NaN on the diagonal and for undefined pairs. */
  p: number[][];
  /** The p-values after the chosen adjustment (equal to `p` for "none"). */
  pAdjusted: number[][];
  n: number[][];
  /** Cases used for each variable's mean and SD (its own non-missing cases). */
  means: number[];
  sds: number[];
  /** Number of cases dropped by listwise deletion; 0 for pairwise. */
  droppedListwise: number;
}

type Column = readonly (number | null)[];

function pairCases(
  a: Column,
  b: Column,
  keep: readonly boolean[] | null
): [number[], number[]] {
  const xs: number[] = [];
  const ys: number[] = [];
  for (let i = 0; i < a.length; i++) {
    const x = a[i];
    const y = b[i];
    if (x !== null && y !== null && (keep === null || keep[i])) {
      xs.push(x);
      ys.push(y);
    }
  }
  return [xs, ys];
}

function pFromR(r: number, n: number): number {
  if (Number.isNaN(r) || n < 3) {
    return Number.NaN;
  }
  if (Math.abs(r) >= 1) {
    return 0;
  }
  return studentTTwoTailed(r * Math.sqrt((n - 2) / (1 - r * r)), n - 2);
}

/** R's `p.adjust` for "bonferroni" and "holm" over a vector of p-values. */
export function adjustP(
  values: readonly number[],
  method: Adjustment
): number[] {
  const m = values.length;
  if (method === "none") {
    return [...values];
  }
  if (method === "bonferroni") {
    return values.map((p) => Math.min(1, p * m));
  }
  const order = values
    .map((p, index) => ({ p, index }))
    .sort((a, b) => a.p - b.p);
  const adjusted = new Array<number>(m);
  let running = 0;
  for (const [rank, item] of order.entries()) {
    running = Math.max(running, Math.min(1, (m - rank) * item.p));
    adjusted[item.index] = running;
  }
  return adjusted;
}

function completeCaseMask(columns: readonly Column[]): boolean[] {
  const total = columns[0]?.length ?? 0;
  return Array.from({ length: total }, (_, i) =>
    columns.every((column) => column[i] !== null)
  );
}

function summary(column: Column): { mean: number; sd: number } {
  const values = column.filter((v): v is number => v !== null);
  const n = values.length;
  if (n === 0) {
    return { mean: Number.NaN, sd: Number.NaN };
  }
  const mean = values.reduce((a, b) => a + b, 0) / n;
  const ss = values.reduce((a, b) => a + (b - mean) ** 2, 0);
  return { mean, sd: n > 1 ? Math.sqrt(ss / (n - 1)) : Number.NaN };
}

function correlate(
  xs: number[],
  ys: number[],
  method: CorrelationMethod
): number {
  if (method === "spearman") {
    return pearson(averageRanks(xs), averageRanks(ys));
  }
  return pearson(xs, ys);
}

function emptyMatrix(k: number, fill: number): number[][] {
  return Array.from({ length: k }, () => new Array<number>(k).fill(fill));
}

export function correlationMatrix(
  columns: readonly Column[],
  options: {
    method?: CorrelationMethod;
    missing?: MissingMode;
    adjustment?: Adjustment;
  } = {}
): MatrixReport | null {
  const k = columns.length;
  const method = options.method ?? "pearson";
  const missing = options.missing ?? "pairwise";
  const adjustment = options.adjustment ?? "none";
  if (k < 2) {
    return null;
  }
  const mask = missing === "listwise" ? completeCaseMask(columns) : null;
  const r = emptyMatrix(k, Number.NaN);
  const p = emptyMatrix(k, Number.NaN);
  const n = emptyMatrix(k, 0);
  const pairs: { i: number; j: number }[] = [];
  for (let i = 0; i < k; i++) {
    r[i][i] = 1;
    n[i][i] = columns[i].filter((v) => v !== null).length;
    for (let j = i + 1; j < k; j++) {
      const [xs, ys] = pairCases(columns[i], columns[j], mask);
      const value = xs.length >= 2 ? correlate(xs, ys, method) : Number.NaN;
      const pValue = pFromR(value, xs.length);
      r[i][j] = value;
      r[j][i] = value;
      p[i][j] = pValue;
      p[j][i] = pValue;
      n[i][j] = xs.length;
      n[j][i] = xs.length;
      pairs.push({ i, j });
    }
  }
  if (mask) {
    const used = mask.filter(Boolean).length;
    for (let i = 0; i < k; i++) {
      n[i][i] = used;
    }
  }
  // Adjust across the distinct pairs that have a p-value.
  const defined = pairs.filter(({ i, j }) => !Number.isNaN(p[i][j]));
  const adjustedValues = adjustP(
    defined.map(({ i, j }) => p[i][j]),
    adjustment
  );
  const pAdjusted = p.map((row) => [...row]);
  for (const [index, { i, j }] of defined.entries()) {
    pAdjusted[i][j] = adjustedValues[index];
    pAdjusted[j][i] = adjustedValues[index];
  }
  const stats = columns.map(summary);
  return {
    k,
    method,
    missing,
    adjustment,
    r,
    p,
    pAdjusted,
    n,
    means: stats.map((s) => s.mean),
    sds: stats.map((s) => s.sd),
    droppedListwise: mask ? mask.length - mask.filter(Boolean).length : 0,
  };
}

/** APA significance stars: * p < .05, ** p < .01, *** p < .001. */
export function stars(p: number): string {
  if (Number.isNaN(p)) {
    return "";
  }
  if (p < 0.001) {
    return "***";
  }
  if (p < 0.01) {
    return "**";
  }
  return p < 0.05 ? "*" : "";
}

export interface RankedPair {
  i: number;
  j: number;
  r: number;
  p: number;
  n: number;
}

/** Distinct pairs, strongest |r| first (undefined pairs last). */
export function rankedPairs(report: MatrixReport): RankedPair[] {
  const pairs: RankedPair[] = [];
  for (let i = 0; i < report.k; i++) {
    for (let j = i + 1; j < report.k; j++) {
      pairs.push({
        i,
        j,
        r: report.r[i][j],
        p: report.pAdjusted[i][j],
        n: report.n[i][j],
      });
    }
  }
  const size = (pair: RankedPair) =>
    Number.isNaN(pair.r) ? -1 : Math.abs(pair.r);
  return pairs.sort((a, b) => size(b) - size(a));
}
